import * as React from 'react';
import classnames from 'classnames';
import { MenuItem } from 'lib/types';
import { sortMenuItems } from 'lib/firebase/util';
import { Link } from 'gatsby';
import LevelAccess from 'assets/level_access_icon.inline.svg';
import CareerLevelAccess from 'assets/career_level_access_icon.inline.svg';
import Logo from 'assets/logoWithText.inline.svg';
import CareerBondvetLogo from 'assets/careerPageLogoWithText.inline.svg';
import AppStoreButton from 'assets/app-store-button.inline.svg';
import InputWithCta from 'core/components/InputWithCta/InputWithCta';
import { useIntl } from 'react-intl';
import * as styles from './Footer.module.scss';
import FooterLink from './FooterLink';
import Social from '../Social';
import useToggleAda from '../../hooks/useToggleAda';
import ExternalLink from '../ExternalLink';
import AccessibeTrigger from '../AccessibeTrigger';
import NewsletterSignUp from '../NewsletterSignUp';
import CallToAction from '../CallToAction';

type FooterProps = {
    prefix: string;
    items: ReadonlyArray<MenuItem>;
    className?: string;
    showLocations?: boolean;
    withTopMargin?: boolean;
    isCareerPage?: boolean;
};

function createLinks(
    prefix: string,
    items: ReadonlyArray<MenuItem>,
): React.ReactNode {
    return (
        <>
            {items.map((item: MenuItem, idx: number) => (
                <React.Fragment
                    key={[item.id, (item.sortOrder || idx).toString(10)].join(
                        '-',
                    )}
                >
                    <li>
                        <FooterLink item={item} prefix={prefix} />
                    </li>
                </React.Fragment>
            ))}
        </>
    );
}

function createTopLinks(
    prefix: string,
    items: ReadonlyArray<MenuItem>,
): ReadonlyArray<React.ReactNode> {
    return sortMenuItems(items).map((item: MenuItem, idx: number) => {
        if (!item || !item.subItems) return null;

        const { id, sortOrder } = item;
        const isLastItem = idx === items.length - 1;

        return (
            <section
                key={[id, (sortOrder || idx).toString(10)].join('-')}
                className={styles.section}
            >
                <h3 className={styles.title}>{item.label}</h3>
                <div>
                    <nav aria-label={item.label}>
                        <ul className={styles.footerList}>
                            {createLinks(prefix, item.subItems)}

                            {isLastItem && (
                                <li className={styles.storeButton}>
                                    {/* eslint-disable-next-line max-len */}
                                    <ExternalLink
                                        aria-label="Download on the App Store"
                                        href="https://apps.apple.com/us/app/bond-vet/id1460269744"
                                    >
                                        <AppStoreButton />
                                    </ExternalLink>
                                </li>
                            )}
                        </ul>
                    </nav>
                </div>
            </section>
        );
    });
}

export default function Footer({
    items,
    prefix,
    className,
    withTopMargin = true,
    isCareerPage,
}: FooterProps) {
    const intl = useIntl();
    const { adaEnabled, toggle: toggleAda } = useToggleAda();

    const { topItems, bottomItems } = React.useMemo(() => {
        const sorted = (items ?? [])
            .slice()
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        return {
            topItems: sorted.slice(0, -1),
            bottomItems: sorted.slice(-1)[0]?.subItems || [],
        };
    }, [items]);

    const topLinks = React.useMemo(
        () => createTopLinks(prefix, topItems),
        [prefix, topItems],
    );

    const bottomLinks = React.useMemo(() => {
        if (bottomItems.length) {
            return createLinks(prefix, bottomItems);
        }
        return null;
    }, [prefix, bottomItems]);

    const inputId = 'mail-signup';

    return (
        <footer
            className={classnames(
                isCareerPage ? styles.careerFooter : styles.footer,
                className,
                {
                    [styles.noTopMargin]: !withTopMargin,
                },
            )}
        >
            <div className={styles.container}>
                <section className={styles.section}>
                    <div>
                        {isCareerPage ? (
                            <CareerBondvetLogo
                                className={styles.logo}
                                role="img"
                                aria-label="Bond Vet logo"
                            />
                        ) : (
                            <Logo
                                className={styles.logo}
                                role="img"
                                aria-label="Bond Vet logo"
                            />
                        )}
                        <p className={styles.subtitle}>
                            Get the scoop about events, promotions, and clinic
                            openings.
                        </p>

                        <NewsletterSignUp
                            inputId={inputId}
                            Component={InputWithCta}
                        />

                        <Social exclude={['tiktok', 'spotify']} />
                    </div>
                </section>
                <div className={styles.linkSection}>
                    <section className={styles.section}>
                        <h3 className={styles.title}>Contact Us</h3>
                        <ul className={styles.contactList}>
                            <li>
                                Text:{' '}
                                <a
                                    className={styles.underlinedLink}
                                    href={`sms:${intl.formatMessage({
                                        id: 'app.textingPhoneNumber',
                                    })}`}
                                >
                                    {intl.formatMessage({
                                        id: 'app.formattedTextingPhoneNumber',
                                    })}
                                </a>
                            </li>
                            {adaEnabled && (
                                <li>
                                    Chat:{' '}
                                    <button
                                        className={styles.adaButton}
                                        onClick={toggleAda}
                                    >
                                        Live Chat
                                    </button>
                                </li>
                            )}

                            <li>
                                <a
                                    className={styles.footerLink}
                                    href="https://discover.bondvet.com/other-inquiries/"
                                >
                                    Other Inquiries
                                </a>
                            </li>
                        </ul>
                    </section>

                    {topLinks}
                </div>
            </div>

            <div className={styles.separator} />

            <div className={`${styles.container} ${styles.bottomContainer}`}>
                <section className={styles.bottomLinksSection}>
                    <ul className={styles.bottomList}>
                        {bottomLinks}
                        <li>
                            <CallToAction
                                onClick={() => window.Cookiebot?.show()}
                            >
                                Manage Consent
                            </CallToAction>
                        </li>
                        <AccessibeTrigger />
                        <li>
                            <Link to="/accessibility-statement">
                                <span
                                    aria-label="Level Access website accessibility icon."
                                    className={styles.levelAccessIcon}
                                >
                                    {isCareerPage ? (
                                        <CareerLevelAccess />
                                    ) : (
                                        <LevelAccess />
                                    )}
                                </span>
                            </Link>
                        </li>
                    </ul>
                </section>

                <section className={classnames(styles.bottom)}>
                    &copy; {new Date().getFullYear()} copyright all rights
                    reserved
                </section>
            </div>
        </footer>
    );
}

Footer.defaultProps = {
    prefix: '',
};
